import { flexRender } from "@tanstack/react-table";
import {
  faAngleLeft,
  faAngleRight,
  faAnglesLeft,
  faAnglesRight,
  faLanguage,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Color } from "@/common/colors/colors";
import SelectInput from "@/common/components/custom/SelectInput";
import Filter from "./components/Filter";
import {
  CustomTable,
  NavigationButtons,
  PageInfo,
  PaginationContainer,
  TableBody,
  TableCell,
  TableHeadCell,
  TableHeader,
  TableRow,
} from "./DataTableFilter.styles";
import useServerPaginatedDataTableFilter from "./hooks/useServerPaginatedDataTableFilter";

const PaginatedDataTableFilter = ({
  data,
  columns,
  rows = [5, 10, 15, 20, 25, 30, 35, 40, 100, 200],
  totalLength,
  setLimit,
  limit,
  setSkip,
  skip,
  onFiltersChange,
  onSortingChange,
}) => {
  const {
    table,
    gotoNext,
    gotoPrev,
    gotoStart,
    gotoEnd,
    currentPage,
    showingFromNumber,
    showingToNumber,
  } = useServerPaginatedDataTableFilter({
    onFiltersChange,
    onSortingChange,
    data,
    columns,
    rows,
    setSkip,
    skip,
    limit,
    totalLength,
  });

  return (
    <div>
      <CustomTable>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHeadCell
                  key={header.id}
                  width={header.getSize()}
                  style={{
                    textAlign: "left",
                  }}
                >
                  {header.isPlaceholder ? null : (
                    <div style={{ display: "flex" }}>
                      <div
                        onClick={header.column.getToggleSortingHandler()}
                        style={{
                          fontSize: "14px",
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {
                          { asc: "🔼", desc: "🔽" }[
                            header.column.getIsSorted() ?? null
                          ]
                        }
                      </div>
                      {header.column?.columnDef?.meta?.aditinalFn && (
                        <div
                          onClick={() =>
                            header.column?.columnDef?.meta?.aditinalFn(
                              (prev) => !prev
                            )
                          }
                          style={{ marginLeft: "10px", cursor: "pointer" }}
                        >
                          <FontAwesomeIcon
                            icon={faLanguage}
                            style={{ fontSize: "18px" }}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {header.column.getCanFilter() ? (
                    <div>
                      <Filter column={header.column} />
                    </div>
                  ) : null}
                </TableHeadCell>
              ))}
            </tr>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id} width={cell.column.getSize()}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </CustomTable>
      <PaginationContainer>
        <PageInfo>
          Showing: {showingFromNumber} to {showingToNumber} of {totalLength}
          <SelectInput
            showLabel={false}
            color={Color.DarkBlue}
            selectWidth={80}
            options={rows.map((r) => ({ label: r, value: r }))}
            selected={{ label: limit, value: limit }}
            setSelected={(value) => {
              setLimit(value.value);
              setSkip((prev) => prev - (prev % value.value));
            }}
          />
        </PageInfo>
        <NavigationButtons>
          <FontAwesomeIcon
            icon={faAnglesLeft}
            style={{ color: Color.DarkBlue, cursor: "pointer" }}
            onClick={gotoStart}
          />
          <FontAwesomeIcon
            icon={faAngleLeft}
            style={{ color: Color.DarkBlue, cursor: "pointer" }}
            onClick={gotoPrev}
          />
          <p>{currentPage}</p>
          <FontAwesomeIcon
            icon={faAngleRight}
            style={{ color: Color.DarkBlue, cursor: "pointer" }}
            onClick={gotoNext}
          />
          <FontAwesomeIcon
            icon={faAnglesRight}
            style={{ color: Color.DarkBlue, cursor: "pointer" }}
            onClick={gotoEnd}
          />
        </NavigationButtons>
      </PaginationContainer>
    </div>
  );
};

export default PaginatedDataTableFilter;
